<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="800">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <div class="d-flex">
                    <h4>Campaign Device Logs: {{ campaign.name }}</h4>
                    <v-btn class="ms-4" icon="mdi-refresh" size="x-small" variant="tonal"
                           @click="()=>{ currentPage=1; fetchCampaignLogs() }"/>
                </div>
                <v-btn density="compact" icon="mdi-close" variant="flat"
                       @click="$emit('update:modelValue', false)"/>
            </v-card-title>

            <v-container>
                <v-row>
                    <v-col cols="4">
                        <v-autocomplete
                            v-model="selectedDeviceId"
                            :items="devices"
                            clearable
                            density="compact"
                            hide-details
                            item-title="slno"
                            item-value="id"
                            label="Filter by Device"
                            variant="outlined"
                            @update:model-value="currentPage = 1; fetchCampaignLogs()"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <thead>
                            <tr>
                                <th>Device</th>
                                <th>File</th>
                                <th>Attempts</th>
                                <th>Progress Code</th>
                                <th>Message</th>
                                <th>Created at</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr
                                v-for="log in logs"
                                v-if="!loading && logs.length"
                                class="text-left"
                            >
                                <td>{{ log.device }}</td>
                                <td>{{ log.file_name }}</td>
                                <td>{{ log.attempts }}</td>
                                <td>{{ log.progress_code }}</td>
                                <td>{{ log.message }}</td>
                                <td>{{ formatDateTime(log.created_at) }}</td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" class="text-center" colspan="6">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else class="text-center" colspan="6">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row v-if="totalPages > 1" justify="center">
                    <v-col cols="8">
                        <v-pagination
                            v-model="currentPage"
                            :length="totalPages"
                            class="my-4"
                            density="compact"
                            @update:model-value="fetchCampaignLogs"
                        ></v-pagination>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn
                    size="small"
                    type="button"
                    @click="$emit('update:modelValue', false);"
                >
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from "@/composables/useTimezone";

const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    campaign: Object,
});
const sortedType = ref(null);
const sortedColumn = ref(null);
const dialogSwitch = ref(props.modelValue);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const logs = ref([]);
const devices = ref([]);
const selectedDeviceId = ref(null);

const emits = defineEmits(["update:modelValue"]);
onMounted(() => {
    fetchDevices();
    fetchCampaignLogs();
});

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchCampaignLogs();
}

const fetchDevices = () => {
    send("get", `admin/ota/campaigns/devices-status/${props.campaign.id}?list=true`)
        .then((response) => {
            devices.value = response.data.data;
        }).catch(() => {});
}

const fetchCampaignLogs = () => {
    loading.value = true;
    const deviceParam = selectedDeviceId.value ? `&device_id=${selectedDeviceId.value}` : '';
    send("get", `admin/ota/campaigns/logs/${props.campaign.id}?page=${currentPage.value}${deviceParam}`)
        .then((response) => {
            logs.value = response.data.data.data;
            totalPages.value = response.data.data.meta.last_page;
            currentPage.value = response.data.data.meta.current_page;
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? "Campaign logs could not be loaded", "error");
        }).finally(() => loading.value = false);
}
</script>
